import { DocumentTypeEnum } from 'api-hooks/order/travel-document';
import { useGetVisaDocuments } from 'api-hooks/visa-document';
import { colors } from 'baseui/tokens';
import { themedStyled } from 'common/styles/styletron.theme';
import { capitalize, string2money } from 'common/utils/string';
import DrawerHeader from 'components/common/drawer/drawer-header';
import { DrawerBody } from 'components/common/drawer/styles';
import Text from 'components/elements/text';
import { format } from 'date-fns';
import {
  AirlineContainer,
  GuestContent,
} from 'legacy-modules/sales/ticket-sale/components/summary/styles';
import {
  FullContainer,
  SeparatorHorizontal,
  SeparatorVertical,
} from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import useRecalculate from '../../hooks/use-recalculate';
import {
  DocumentSaleGuestDetailFormType,
  DocumentSaleFormType,
} from '../form-type';

interface Props {
  onClose: () => void;
}

function SummaryDetailItem(props: {
  data: DocumentSaleGuestDetailFormType;
  documentType: string;
}) {
  const { data, documentType } = props;
  const { t } = useTranslation();
  const { data: visaData } = useGetVisaDocuments();
  const label =
    data.label ||
    visaData?.data?.find((item) => item.id === data.visaDocumentId)?.name;

  const visaLabel = [
    label,
    data.appointmentAt && data.biometric
      ? format(data.appointmentAt, '| dd/MM/yyyy | HH:mm')
      : undefined,
  ]
    .filter(Boolean)
    .join(' ');

  const passportLabel = [capitalize(data.type), data.description]
    .filter(Boolean)
    .join(' | ');

  const date = [
    [
      data.startAt ? format(data.startAt, 'dd-MM-yyyy') : '',
      data.endAt ? format(data.endAt, 'dd-MM-yyyy') : '',
    ].join(' - '),
    data.days ? `(${data.days} ${t('common:days')})` : '',
  ].join(' ');

  const insuranceLabel = [capitalize(data.type), date]
    .filter(Boolean)
    .join(' | ');

  return (
    <>
      <FullContainer
        $style={{
          paddingLeft: '24px',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Text
          variant="ParagraphSmall"
          $style={{
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }}
        >
          {documentType === DocumentTypeEnum.visa
            ? visaLabel
            : documentType === DocumentTypeEnum.passport
            ? passportLabel
            : insuranceLabel}
        </Text>
        <SeparatorVertical $margin={8} />
        <Text variant="LabelSmall">Rp {string2money(data.price || 0)}</Text>
      </FullContainer>
      <SeparatorVertical $margin={16} />
    </>
  );
}

function Total({ label, price }: { label: string; price: number }) {
  return (
    <TotalContent>
      <TotalItemContent>
        <Text variant="LabelMedium">{label}:</Text>
        <SeparatorHorizontal $margin={8} />
        <Text variant="HeadingXSmall">{`Rp ${string2money(price)}`}</Text>
      </TotalItemContent>
    </TotalContent>
  );
}

export default function SummaryDrawer(props: Props) {
  const { onClose } = props;
  const { t } = useTranslation();
  const { control } = useFormContext<DocumentSaleFormType>();
  const [documentSales] = useWatch({
    control,
    name: ['documentSales'],
  });
  const cost = useRecalculate();

  return (
    <>
      <DrawerHeader
        onClose={onClose}
        title={t('document:travel_document_sales_details')}
      />

      <DrawerBody>
        {documentSales?.map((documentSale) => {
          const documentType = documentSale.documentType;
          const documentSaleGuests = documentSale.documentSaleGuests || [];
          const documentSaleAddOns = documentSale.documentSaleAddOns || [];

          return (
            <React.Fragment key={documentSale.id}>
              {documentType && (
                <Text variant="HeadingXSmall">
                  {t(`common:${documentType}`)}
                </Text>
              )}
              <SeparatorVertical $margin={24} />
              {documentSaleGuests.map((guest) => {
                const addOns = documentSaleAddOns.filter(
                  (addOn) => addOn.guestId === guest.guestId,
                );

                return (
                  <>
                    <Text variant="LabelSmall">{guest.guestName || ''}</Text>
                    <SeparatorVertical $margin={8} />
                    <>
                      {guest.details?.map((detail) => (
                        <SummaryDetailItem
                          key={detail.customId}
                          data={detail}
                          documentType={documentType}
                        />
                      ))}
                    </>
                    {addOns.length > 0 && (
                      <>
                        <Text variant="ParagraphSmall" color={colors.gray500}>
                          {t('ticketSales:add_ons')}
                        </Text>
                        {addOns.map((addOn, idx) => (
                          <AirlineContainer
                            key={guest.guestId || `${0}-addons-${idx}`}
                          >
                            <GuestContent>
                              <Text variant="LabelSmall">
                                {addOn.description?.toUpperCase() || '-'}
                              </Text>
                            </GuestContent>
                            <Text variant="LabelSmall">
                              {`Rp ${string2money(addOn.price || 0)}`}
                            </Text>
                          </AirlineContainer>
                        ))}
                      </>
                    )}
                    <BorderDivider />
                    <SeparatorVertical $margin={16} />
                  </>
                );
              })}
            </React.Fragment>
          );
        })}

        {/* <div>
          {!!documentSaleAddOns.length && (
            <Text variant="ParagraphSmall" color={colors.gray500}>
              {t('ticketSales:add_ons')}
            </Text>
          )}
          {documentSaleAddOns.map((guest, idx) => {
            const label = [guest.guestName, guest.description]
              .filter(Boolean)
              .join(' | ');
            return (
              <>
                <AirlineContainer key={guest.guestId || `${0}-addons-${idx}`}>
                  <GuestContent>
                    <Text variant="LabelSmall">{label}</Text>
                  </GuestContent>
                  <Text variant="LabelSmall">
                    {`Rp ${string2money(guest.price || 0)}`}
                  </Text>
                </AirlineContainer>
              </>
            );
          })}
          <SeparatorVertical $margin={24} />
          <BorderDivider />
        </div> */}
        <Total label={t('common:subtotal')} price={cost.subtotal} />
        <Total label={t('common:discount')} price={cost.discount} />
        <Total label={t('common:other_cost')} price={cost.otherCost} />
        <Total label={t('common:total')} price={cost.total} />
      </DrawerBody>
    </>
  );
}

const TotalContent = themedStyled('div', {
  display: 'flex',
  justifyContent: 'end',
});

const TotalItemContent = themedStyled('div', ({ $theme }) => ({
  display: 'flex',
  marginTop: '8px',
  marginBottom: '8px',
  justifyContent: 'space-between',
  alignItems: 'center',
  minWidth: '100%',
  [$theme.mediaQuery.md]: {
    minWidth: '50%',
    maxWidth: '500px',
  },
}));

const BorderDivider = themedStyled('div', {
  // display: 'flex',
  // justifyContent: 'end',
  height: '2px',
  borderBottom: '1px solid #E2E2E2',
});
